// Mirror gateway/prof-figure-context.ts. Only canonical references cross the wire.
export type ProfFigureReference =
  | { kind: 'question'; questionId: string }
  | { kind: 'asset'; imageRef: string };

export type ProfFigureCommand = {
  mutationId: string;
  expectedRevision: number;
} & (
  | { action: 'set'; reference: ProfFigureReference }
  | { action: 'clear' }
);

export interface ProfResolvedFigure {
  reference: ProfFigureReference;
  imageRef: string;
  label: string;
}

export interface ProfFigureSnapshot {
  revision: number;
  figure: ProfResolvedFigure | null;
  lastMutationId: string | null;
}

export interface ProfFigureEvent {
  sessionId: string;
  snapshot: ProfFigureSnapshot;
  imageStatus: 'not_loaded' | 'available' | 'unavailable';
}

/** A local preview is display-only; the server resolves the reference itself. */
export interface ProfFigureSelection {
  reference: ProfFigureReference;
  imageRef: string;
  label: string;
}

export interface PendingProfFigure {
  command: ProfFigureCommand;
  preview: ProfResolvedFigure | null;
  status: 'ready' | 'saving' | 'error';
}

export const EMPTY_PROF_FIGURE: ProfFigureSnapshot = {
  revision: 0, figure: null, lastMutationId: null,
};

/** Render local paths only, including before the server acknowledges a selection. */
export function profFigureAssetRef(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const ref = value.trim().replace(/^\/(?!\/)/, '');
  return /^(?:img_sign_by_hash\/[a-f0-9]+|img_sign\/[A-Za-z0-9_-]+|img_diagram\/[A-Za-z0-9_-]+)\.png$/i.test(ref)
    ? ref : null;
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseProfFigureReference(value: unknown): ProfFigureReference | null {
  if (!record(value)) return null;
  if (value.kind === 'asset') {
    const imageRef = profFigureAssetRef(value.imageRef);
    return imageRef ? { kind: 'asset', imageRef } : null;
  }
  if (value.kind === 'question' && typeof value.questionId === 'string'
    && value.questionId.length > 0 && value.questionId.length <= 160) {
    return { kind: 'question', questionId: value.questionId };
  }
  return null;
}

export function parseProfFigureSnapshot(value: unknown): ProfFigureSnapshot | null {
  if (!record(value) || !Number.isSafeInteger(value.revision) || (value.revision as number) < 0
    || (value.lastMutationId !== null && typeof value.lastMutationId !== 'string')) return null;
  let figure: ProfResolvedFigure | null = null;
  if (value.figure !== null) {
    if (!record(value.figure)) return null;
    const reference = parseProfFigureReference(value.figure.reference);
    const imageRef = profFigureAssetRef(value.figure.imageRef);
    if (!reference || !imageRef || typeof value.figure.label !== 'string') return null;
    figure = { reference, imageRef, label: value.figure.label.slice(0, 240) };
  }
  return { revision: value.revision as number, figure, lastMutationId: value.lastMutationId as string | null };
}

export function parseProfFigureEvent(value: unknown): ProfFigureEvent | null {
  if (!record(value) || typeof value.sessionId !== 'string') return null;
  const snapshot = parseProfFigureSnapshot(value.snapshot);
  if (!snapshot || !['not_loaded', 'available', 'unavailable'].includes(String(value.imageStatus))) return null;
  return { sessionId: value.sessionId, snapshot, imageStatus: value.imageStatus as ProfFigureEvent['imageStatus'] };
}


export function parsePendingProfFigure(value: unknown): PendingProfFigure | null {
  if (!record(value) || !record(value.command)) return null;
  const { mutationId, expectedRevision, action } = value.command;
  if (typeof mutationId !== 'string' || !mutationId || !Number.isSafeInteger(expectedRevision)
    || (expectedRevision as number) < 0 || !['ready', 'saving', 'error'].includes(String(value.status))) return null;
  const status = value.status === 'saving' ? 'error' : value.status as PendingProfFigure['status'];
  const base = { mutationId, expectedRevision: expectedRevision as number };
  if (action === 'clear') return { command: { ...base, action }, preview: null, status };
  if (action !== 'set') return null;
  const reference = parseProfFigureReference(value.command.reference);
  const snapshot = parseProfFigureSnapshot({ revision: 0, lastMutationId: null, figure: value.preview });
  if (!reference || !snapshot?.figure) return null;
  return { command: { ...base, action, reference }, preview: snapshot.figure, status };
}
